import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ImagePlus, LifeBuoy, Loader2, Send, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { api } from '@/services/api';
import { ApiError } from '@/services/httpClient';
import {
  ACCEPTED_IMAGE_TYPES,
  ImageUploadError,
  MAX_IMAGE_SIZE_BYTES,
  uploadImage,
} from '@/lib/imageUpload';
import { buildDiagnosticsBundle, getRecentErrorCount } from '@/lib/supportDiagnostics';
import { getSentryLastEventId } from '@/lib/sentryInit';

type SupportCategory = 'bug' | 'feedback' | 'other';

const MAX_ATTACHMENTS = 5;

interface PendingAttachment {
  id: string;
  file: File;
  previewUrl: string;
}

export default function SupportReportPage() {
  const { t, language } = useLanguage();
  const { user, firebaseUser, selectedCompany } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Backend User uses `id`; Firebase Auth uses `uid` (required for Storage rules).
  const uid = firebaseUser?.uid ?? user?.id;

  const reportId = useMemo(() => crypto.randomUUID(), []);
  const [category, setCategory] = useState<SupportCategory>('bug');
  const [message, setMessage] = useState('');
  const [consent, setConsent] = useState(false);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);

  useEffect(() => {
    document.title =
      language === 'it' ? 'Segnalazione — HiroMatch' : 'Report an issue — HiroMatch';
  }, [language]);

  const revokePreviews = useCallback((items: PendingAttachment[]) => {
    for (const a of items) URL.revokeObjectURL(a.previewUrl);
  }, []);

  useEffect(() => {
    return () => revokePreviews(attachments);
  }, [attachments, revokePreviews]);

  function handleFilesSelected(files: FileList | null) {
    if (!files?.length || !uid) return;
    const remaining = MAX_ATTACHMENTS - attachments.length;
    if (remaining <= 0) {
      toast.error(t('support.attachments.max'));
      return;
    }
    const toAdd: PendingAttachment[] = [];
    for (let i = 0; i < Math.min(files.length, remaining); i++) {
      const file = files[i]!;
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast.error(t('support.attachments.invalidType'));
        continue;
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        toast.error(t('support.attachments.tooLarge'));
        continue;
      }
      toAdd.push({
        id: `${Date.now()}-${i}`,
        file,
        previewUrl: URL.createObjectURL(file),
      });
    }
    if (toAdd.length > 0) setAttachments((prev) => [...prev, ...toAdd]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removeAttachment(id: string) {
    setAttachments((prev) => {
      const removed = prev.find((a) => a.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!uid || !user?.email) {
      toast.error(t('support.errorGeneric'));
      return;
    }
    if (!consent) {
      toast.error(t('support.consentRequired'));
      return;
    }
    if (message.trim().length < 10) {
      toast.error(t('support.messageTooShort'));
      return;
    }

    setSubmitting(true);
    try {
      const attachmentUrls: string[] = [];
      for (const att of attachments) {
        const url = await uploadImage(att.file, {
          prefix: 'support-attachments',
          ownerId: uid,
          reportId,
        });
        attachmentUrls.push(url);
      }

      const sentryEventId = await getSentryLastEventId();
      const diagnostics = buildDiagnosticsBundle({
        selectedCompanyId: selectedCompany?.id,
        accountType: user.accountType,
        planId: user.billing?.planId,
        sentryEventId,
      });

      await api.auth.submitSupportReport({
        reportId,
        category,
        message: message.trim(),
        diagnostics,
        attachmentUrls,
        locale: language === 'it' ? 'it' : 'en',
      });

      setSent(true);
      toast.success(t('support.success'));
    } catch (err) {
      if (err instanceof ImageUploadError) {
        toast.error(
          err.code === 'too_large'
            ? t('support.attachments.tooLarge')
            : err.code === 'invalid_type'
              ? t('support.attachments.invalidType')
              : t('support.attachments.uploadFailed'),
        );
      } else if (err instanceof ApiError && err.status === 429) {
        toast.error(t('support.errorRateLimit'));
      } else if (err instanceof ApiError && (err.status === 502 || err.status === 503)) {
        toast.error(t('support.errorUnavailable'));
      } else if (err instanceof ApiError && err.status === 400) {
        toast.error(err.message || t('support.errorGeneric'));
      } else if (err instanceof ApiError) {
        toast.error(err.message || t('support.errorGeneric'));
      } else {
        toast.error(t('support.errorGeneric'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const recentErrors = getRecentErrorCount();

  if (sent) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LifeBuoy className="h-5 w-5 text-brand-accent" />
              {t('support.successTitle')}
            </CardTitle>
            <CardDescription>{t('support.successDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button type="button" variant="outline" onClick={() => {
              setSent(false);
              setMessage('');
              setCategory('bug');
              setConsent(false);
              revokePreviews(attachments);
              setAttachments([]);
            }}>
              {t('support.sendAnother')}
            </Button>
            <Button type="button" onClick={() => navigate('/dashboard')}>
              {t('support.backToDashboard')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <LifeBuoy className="h-7 w-7 text-brand-accent" />
          {t('support.title')}
        </h1>
        <p className="text-muted-foreground mt-2 text-sm">{t('support.subtitle')}</p>
      </header>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="support-category">{t('support.categoryLabel')}</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as SupportCategory)}>
                <SelectTrigger id="support-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bug">{t('support.category.bug')}</SelectItem>
                  <SelectItem value="feedback">{t('support.category.feedback')}</SelectItem>
                  <SelectItem value="other">{t('support.category.other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="support-message">{t('support.messageLabel')}</Label>
              <Textarea
                id="support-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('support.messagePlaceholder')}
                rows={6}
                maxLength={5000}
                required
              />
              <p className="text-xs text-muted-foreground text-right">
                {message.length}/5000
              </p>
            </div>

            <div className="space-y-2">
              <Label>{t('support.attachmentsLabel')}</Label>
              <p className="text-xs text-muted-foreground">{t('support.attachmentsHint')}</p>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES.join(',')}
                multiple
                className="hidden"
                onChange={(e) => handleFilesSelected(e.target.files)}
              />
              <div className="flex flex-wrap gap-2">
                {attachments.map((att) => (
                  <div key={att.id} className="relative h-20 w-20 rounded-md border overflow-hidden">
                    <img src={att.previewUrl} alt="" className="h-full w-full object-cover" />
                    <button
                      type="button"
                      className="absolute top-0.5 right-0.5 rounded-full bg-background/90 p-0.5 shadow"
                      onClick={() => removeAttachment(att.id)}
                      aria-label={t('support.attachments.remove')}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {attachments.length < MAX_ATTACHMENTS && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-20 w-20 flex flex-col gap-1"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <ImagePlus className="h-5 w-5" />
                    <span className="text-[10px]">{t('support.attachments.add')}</span>
                  </Button>
                )}
              </div>
            </div>

            <Collapsible open={diagOpen} onOpenChange={setDiagOpen}>
              <CollapsibleTrigger asChild>
                <Button type="button" variant="ghost" size="sm" className="px-0 h-auto font-normal text-muted-foreground hover:text-foreground">
                  {t('support.diagnostics.toggle')}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 text-sm text-muted-foreground space-y-1 pl-0">
                <ul className="list-disc pl-5 space-y-1">
                  <li>{t('support.diagnostics.route')}: <code className="text-xs">{window.location.pathname}</code></li>
                  <li>{t('support.diagnostics.release')}: {(import.meta.env.VITE_RELEASE_TAG as string) || '—'}</li>
                  {selectedCompany?.name && (
                    <li>{t('support.diagnostics.company')}: {selectedCompany.name}</li>
                  )}
                  <li>
                    {t('support.diagnostics.errors')}: {recentErrors > 0 ? recentErrors : t('support.diagnostics.none')}
                  </li>
                </ul>
              </CollapsibleContent>
            </Collapsible>

            <div className="flex items-start gap-2">
              <Checkbox
                id="support-consent"
                checked={consent}
                onCheckedChange={(v) => setConsent(v === true)}
              />
              <Label htmlFor="support-consent" className="text-sm font-normal leading-snug cursor-pointer">
                {t('support.consentLabel')}
              </Label>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={submitting || !consent}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {t('support.submitting')}
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                {t('support.submit')}
              </>
            )}
          </Button>
          <Button type="button" variant="ghost" asChild>
            <Link to="/dashboard">{t('common.cancel')}</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}
